import { useGlobalStore } from '../stores/globalStore';

/**
 * The menu header a menu cycle lookup is scoped to, or null for the facility's active cycle.
 *
 * An explicit override from the caller wins (the Employee reports pass the facility's Employee
 * menu). Otherwise the user's Temp Menu applies, as WinForms `GetMenuCycleInUseInfo` reads
 * `g_FKey_Menu_Hdr_Override` first: every lookup of "the menu in use" follows the Temp Menu for
 * the rest of the user's session. `tempOverride` defaults to the store's current value.
 */
export function effectiveMenuHeaderOverride(
  explicitOverride?: number | null,
  tempOverride: number | null = useGlobalStore.getState().tempMenuOverrideId,
): number | null {
  if (explicitOverride != null && explicitOverride > 0) return explicitOverride;
  if (tempOverride != null && tempOverride > 0) return tempOverride;
  return null;
}

/**
 * Report settings key the render host reads the Temp Menu from (VB `MenuCycleTempOverride`).
 * Generators that work out the menu from the date on the server use this menu instead
 * of the facility's active cycle, for that report only.
 */
export const TEMP_MENU_SETTINGS_KEY = 'FKey_Menu_Hdr_TempOverride';

/**
 * Whether a report can use the Temp Menu: `true` (the default) when every menu lookup the
 * report's generator makes follows the menu, start date and menu day the website sends;
 * `false`, or a check on the form values and facility settings, when the generator (or part
 * of it) works the menu out from the date on the server instead.
 *
 * The render hosts that read the Temp Menu from the settings (VB `MenuCycleTempOverride`, read
 * by the 4-argument `GetMenuCycleInUseInfo`) are not deployed yet, so on those servers a
 * date lookup returns the facility's ACTIVE cycle. A report that mixed the two would print
 * the Temp Menu's items on the active cycle's days (or nothing). A report that cannot use the
 * Temp Menu is therefore sent the active cycle only, with no Temp Menu in its settings, so it
 * prints the active cycle the same way before and after that deploy, and its Menu Cycle panel
 * says the Temp Menu is not used.
 */
export type TempMenuSupport =
  | boolean
  | ((values: Record<string, unknown>, facilitySettings: Record<string, unknown> | null) => boolean);

/** True when the report uses the Temp Menu for these form values (see {@link TempMenuSupport}). */
export function reportUsesTempMenu(
  support: TempMenuSupport | undefined,
  values: Record<string, unknown>,
  facilitySettings: Record<string, unknown> | null,
): boolean {
  if (support === undefined || support === true) return true;
  if (support === false) return false;
  try {
    return support(values, facilitySettings) !== false;
  } catch {
    // A check that cannot decide keeps the report on the active cycle: never mixed.
    return false;
  }
}

/** Shown in the Menu Cycle panel of a report that cannot use the Temp Menu yet. */
export const TEMP_MENU_NOT_USED_TEXT = 'Temp menu not used by this report yet';

/** Cache key of `useMenuCycleInfo`; `overrideKey` is the effective menu header (null = the active cycle). */
export function menuCycleInfoKey(facilityId: number, dateIso: string | null, overrideKey: number | null) {
  return ['menuCycleInfo', facilityId, dateIso, overrideKey] as const;
}
